/**
 * Roll feed — pure logic + a tiny module-level store behind the docked
 * RollFeed panel (dev handoff 2026-08-24, `vtt-dm-roll-visibility`).
 *
 * The DM's problem: rolls happen on D&D Beyond, on physical dice and in the
 * VTT, and mid-scene he has to ask "what did you roll on?" and alt-tab. The
 * feed is one timestamped list every roll lands in, visible from any view.
 *
 * This module is deliberately React-free so the parsing / nat-20 detection /
 * manual-roll shaping can be unit-tested. The component subscribes to
 * `rollFeedStore` via useSyncExternalStore.
 */
import { parseDiceExpression } from './diceEngine';
import { stripEmailDomain } from './displayName';
import {
  SessionActions,
  type LogEntryPayload,
  type RollRequest,
  type RollSource,
} from './sessionProtocol';

/** History cap — long enough for a combat, small enough to stay light. */
export const ROLL_FEED_CAP = 100;

export type NaturalOutcome = 'nat20' | 'nat1' | null;

export interface RollFeedEntry {
  id: string;
  /** ISO timestamp from the audit row (server clock). */
  ts: string;
  /** Raw actor handle as the server sent it (sub / email / username). */
  actorUsername: string;
  /** Display name for the roller's account (email domain stripped). */
  actorLabel: string;
  /** Character name when known (client-sent, else resolved from the roster). */
  characterName: string | null;
  characterId: string | null;
  /** The reason / roll type — "Athletics check", "Fire Bolt damage". */
  reason: string | null;
  expression: string;
  results: number[];
  total: number;
  modifier: number;
  whisper: boolean;
  source: RollSource;
  natural: NaturalOutcome;
  /** True when the roll was a d20 roll (drives the natural-die emphasis). */
  isD20: boolean;
}

/** Roster shape the resolver needs — narrower than CharacterBundle. */
export interface RollFeedRosterEntry {
  id: string;
  ownerSub?: string | null;
  ownerUsername?: string | null;
  profile?: { name?: string } | null;
}

/**
 * Detect a natural 20 / natural 1.
 *
 * Only d20 rolls count. For a single d20 the die IS the outcome. For several
 * d20s in one expression (advantage rolled as "2d20", or a bulk initiative)
 * any 20 is exciting enough to flag; a natural 1 only when EVERY d20 came up
 * 1 — "2d20 → [1, 17]" is not a fumble.
 *
 * Results are in roll order and groups are rolled in expression order, so the
 * first `count` results of the first d20 group belong to that group.
 */
export function detectNatural(expression: string, results: number[]): { natural: NaturalOutcome; isD20: boolean } {
  let parsed;
  try {
    parsed = parseDiceExpression(expression);
  } catch {
    return { natural: null, isD20: false };
  }
  const d20Values: number[] = [];
  let cursor = 0;
  for (const g of parsed.groups) {
    const slice = results.slice(cursor, cursor + g.count);
    cursor += g.count;
    if (g.faces === 20) d20Values.push(...slice);
  }
  if (d20Values.length === 0) return { natural: null, isD20: false };
  if (d20Values.some((v) => v === 20)) return { natural: 'nat20', isD20: true };
  if (d20Values.every((v) => v === 1)) return { natural: 'nat1', isD20: true };
  return { natural: null, isD20: true };
}

/** Modifier = total − sum(dice). Tolerates a missing/odd payload. */
export function inferModifier(results: number[] | undefined, total: number | undefined): number {
  if (!results || total == null) return 0;
  const sum = results.reduce((a, b) => a + b, 0);
  return total - sum;
}

/**
 * Resolve a character name for a log entry the server didn't tag. Matches
 * the roster on owner sub OR owner handle (email local part) so pre-upgrade
 * servers — which only send `actorUsername` — still show a character name
 * for single-character players. Ambiguous (multi-character) owners fall back
 * to null; the actor label still identifies the player.
 */
export function resolveCharacterName(
  actorUsername: string,
  roster: RollFeedRosterEntry[] | undefined,
): string | null {
  if (!roster || !actorUsername) return null;
  const handle = stripEmailDomain(actorUsername).toLowerCase();
  const matches = roster.filter((b) => {
    if (b.ownerSub && b.ownerSub === actorUsername) return true;
    const owner = stripEmailDomain(b.ownerUsername ?? '').toLowerCase();
    return owner.length > 0 && owner === handle;
  });
  if (matches.length !== 1) return null;
  return matches[0].profile?.name ?? null;
}

/** Shape a `session/log-entry` roll broadcast into a feed entry. Returns null for non-roll entries. */
export function toRollFeedEntry(
  payload: LogEntryPayload,
  roster?: RollFeedRosterEntry[],
): RollFeedEntry | null {
  if (!payload || payload.kind !== 'roll') return null;
  const expression = payload.expression ?? '';
  const results = Array.isArray(payload.results) ? payload.results : [];
  const total = payload.total ?? results.reduce((a, b) => a + b, 0);
  const { natural, isD20 } = detectNatural(expression, results);
  const characterName =
    (payload.characterName && payload.characterName.trim()) ||
    resolveCharacterName(payload.actorUsername, roster);
  return {
    id: payload.id,
    ts: payload.ts,
    actorUsername: payload.actorUsername,
    actorLabel: stripEmailDomain(payload.actorUsername) || '?',
    characterName: characterName || null,
    characterId: payload.characterId ?? null,
    reason: payload.reason?.trim() || null,
    expression,
    results,
    total,
    modifier: inferModifier(results, total),
    whisper: payload.whisper === true,
    source: payload.source === 'manual' ? 'manual' : 'sheet',
    natural,
    isD20,
  };
}

export interface ManualRollInput {
  /** What was rolled — "Attack", "Perception check"… Free text. */
  type: string;
  /** Final total after modifiers. */
  total: number;
  /**
   * Natural die result (before modifiers). Optional for damage-style rolls;
   * when present the expression is rebuilt as 1d<faces>±mod so the feed can
   * flag nat 20 / nat 1 exactly like an in-app roll.
   */
  natural?: number | null;
  /** Die faces for the natural result; defaults to 20. */
  faces?: number;
  characterId?: string | null;
  characterName?: string | null;
  whisper?: boolean;
}

/**
 * Build the `session/roll` request for an off-app roll so it rides the SAME
 * server path as an in-app roll (audit row + broadcast). The dice "results"
 * are the self-reported natural (or the total when no natural was given);
 * the server never re-rolls, so this is exactly as trustworthy as the sheet
 * roller — the audit row carries `source: manual` so nobody mistakes it.
 *
 * Throws on nonsense input so the form can show the message inline.
 */
export function buildManualRollRequest(input: ManualRollInput): RollRequest {
  const type = input.type.trim();
  if (!type) throw new Error('Say what you rolled (e.g. "Attack", "Stealth check").');
  if (!Number.isFinite(input.total)) throw new Error('Total must be a number.');
  const total = Math.trunc(input.total);
  const faces = input.faces && input.faces > 0 ? Math.trunc(input.faces) : 20;

  let expression: string;
  let results: number[];
  if (input.natural != null && Number.isFinite(input.natural)) {
    const natural = Math.trunc(input.natural);
    if (natural < 1 || natural > faces) {
      throw new Error(`Natural must be between 1 and ${faces}.`);
    }
    const mod = total - natural;
    expression = mod === 0 ? `1d${faces}` : `1d${faces}${mod > 0 ? '+' : '-'}${Math.abs(mod)}`;
    results = [natural];
  } else {
    // No natural given (damage, healing, "I rolled a 14 total"): record the
    // total as the single die value so results is non-empty and total is
    // consistent. The expression tells the truth about how it was captured.
    expression = 'manual';
    results = [total];
  }

  return {
    action: SessionActions.Roll,
    expression,
    results,
    total,
    reason: type.slice(0, 80),
    whisper: input.whisper ?? false,
    characterId: input.characterId || undefined,
    characterName: input.characterName?.trim() || undefined,
    source: 'manual',
  };
}

/** Common roll types for the log-a-roll datalist. Free text still allowed. */
export const MANUAL_ROLL_TYPES: readonly string[] = [
  'Attack',
  'Damage',
  'Saving throw',
  'Ability check',
  'Skill check',
  'Initiative',
  'Death save',
  'Concentration',
  'Healing',
];

// ─────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────

type Listener = () => void;

export interface RollFeedSnapshot {
  entries: readonly RollFeedEntry[];
  /** Entries that arrived while the panel was collapsed. */
  unread: number;
}

/**
 * Newest-first ring of feed entries with an unread counter. Module-level so
 * the socket listener (mounted once in the shell) and the panel component
 * don't need a React context between them, mirroring `toastStore`.
 */
export class RollFeedStore {
  private snapshot: RollFeedSnapshot = { entries: [], unread: 0 };
  private listeners = new Set<Listener>();
  private countUnread = true;
  private readonly cap: number;

  constructor(cap: number = ROLL_FEED_CAP) {
    this.cap = cap;
  }

  get(): RollFeedSnapshot {
    return this.snapshot;
  }

  /** Add an entry (newest first). Duplicate ids — a socket reconnect echo — are ignored. */
  push(entry: RollFeedEntry): boolean {
    if (this.snapshot.entries.some((e) => e.id === entry.id)) return false;
    const entries = [entry, ...this.snapshot.entries].slice(0, this.cap);
    const unread = this.countUnread ? this.snapshot.unread + 1 : this.snapshot.unread;
    this.snapshot = { entries, unread };
    this.notify();
    return true;
  }

  /** While the panel is expanded, arrivals don't accrue as unread. */
  setCountUnread(count: boolean): void {
    this.countUnread = count;
    if (!count) this.markRead();
  }

  markRead(): void {
    if (this.snapshot.unread === 0) return;
    this.snapshot = { ...this.snapshot, unread: 0 };
    this.notify();
  }

  clear(): void {
    this.snapshot = { entries: [], unread: 0 };
    this.notify();
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  }

  private notify(): void {
    for (const l of [...this.listeners]) l();
  }
}

export const rollFeedStore = new RollFeedStore();
